import PropTypes from 'prop-types';
import maxBy from 'lodash/maxBy';
import { useHistory } from 'react-router-dom';
import productUtils from '../../utils/ProductUtils';
import { PLAN_BLOCKER_CATEGORIES, SELF_SERVE_AUDIENCE_THRESHOLD } from '../../utils/constants';
import formatNumber from '../../utils/format-number';
import { evaluateProductEligibility } from '../../utils/plan-eligibility';
import {
    buildRequirementsViewModel,
    REQUIREMENT_ACTION_KINDS,
} from '../../utils/plan-requirements';
import { CARD_COPY, CONTACT_TEAM_COPY } from '../../utils/plan-requirements-copy';
import parentWindowDataChannel from '../../data/ParentWindowDataChannel';
import PlanRequirementsModal from './PlanRequirementsModal';

function Feature({ feature }) {
    return <li>{feature}</li>;
}

// Render the features string and include the max. possible members amount
function Features({ features }) {
    return features.map((feature, index) => <Feature key={index} feature={feature} />);
}

// The over-the-top-step handoff shown by non-entry plans.
function ContactTeam() {
    return (
        <div className="price-more">
            <p>
                Over <strong>{formatNumber(SELF_SERVE_AUDIENCE_THRESHOLD)}</strong> members?
            </p>
            <p>
                <a href={CONTACT_TEAM_COPY.href}>{CONTACT_TEAM_COPY.action}</a>{' '}
                {CONTACT_TEAM_COPY.detail}
            </p>
        </div>
    );
}

export default function Product({
    productName,
    stripeProductId,
    handlePlanSelected,
    showPeriod,
    addons,
    isSubdirectory,
    prices,
    currentMembers,
    currentSiteLimits,
    limits,
    currentPrice,
    currentGhostVersion,
    features,
    hasCustomSendingDomain,
    availableProducts,
    currentPlanName,
    recommendedPlanName,
    recommendedIsCurrentPlan,
    isRecommended,
}) {
    const history = useHistory();

    const {
        suitablePrices,
        suitablePrice,
        availableAddonPrices,
        blockers,
        disableProduct: btnDisabled,
        validationMsg,
    } = evaluateProductEligibility({
        product: {
            name: productName,
            prices,
            limits,
            addons,
        },
        availableProducts,
        currentMembers,
        currentSiteLimits,
        showPeriod,
        currentPrice,
        isSubdirectory,
        currentGhostVersion,
        hasCustomSendingDomain,
    });

    // This is to render the max. possible members number for the same price range
    const sameCostPrices = suitablePrices.filter((price) => price.cost === suitablePrice?.cost);
    const maxMembersForPrice = maxBy(sameCostPrices, (price) => price.limits[0].value);

    const isLastStepAndNotEntryProduct = productUtils.isLastStepAndNotEntryProduct({
        currentMembers,
        productName,
        availableProducts,
    });

    const isCurrentPlanCard = blockers.some(
        (blocker) => blocker.category === PLAN_BLOCKER_CATEGORIES.CURRENT_PLAN,
    );

    const requirementsViewModel = buildRequirementsViewModel({
        blockers,
        productName,
        currentPlanName,
        currentSiteLimits,
        productLimits: limits,
        prices,
        showPeriod,
        availableProducts,
        recommendedPlanName,
        recommendedIsCurrentPlan,
    });

    // When no price fits the audience there is no suitable price; show the
    // plan's fallback price instead of a misleading $0/mo on the blocked card
    const displayPrice = suitablePrice || productUtils.getFallbackPrice({ prices, showPeriod });
    const cost = productUtils.calculateTotalCost({ suitablePrice: displayPrice, showPeriod });

    const { productFeatures, addonFeatures } = productUtils.getFeatures({
        suitablePrice,
        maxMembersForPrice,
        features,
        isLastStepAndNotEntryProduct,
        productName,
        prices,
        showPeriod,
        availableAddonPrices,
        isSubdirectory,
        availableProducts,
    });

    const isLastIncrementStep = productUtils.isLastIncrementStep({
        currentMembers,
        availableProducts,
    });
    // Also disable any product that doesn't suit the selected members
    const shouldDisablePlan = !suitablePrice || btnDisabled;
    // Blocked and current-plan cards keep full-opacity headers; plan-disabled
    // (which fades the header) only remains for the legacy no-blocker edge.
    let cardStateClass = '';
    if (requirementsViewModel || isCurrentPlanCard) {
        cardStateClass = ' plan-blocked';
    } else if (shouldDisablePlan) {
        cardStateClass = ' plan-disabled';
    }

    const handleSelect = () => {
        handlePlanSelected(stripeProductId, showPeriod);
    };

    /**
     * Routes a resolved requirement action to its destination. Ghost Admin
     * owns its settings screens, so admin-settings actions are sent over the
     * parent-window postMessage channel; billing routes navigate within this
     * app's router. The modal has already closed itself when this runs.
     * @param {import('../../utils/plan-requirements').RequirementAction} action
     * @returns {void}
     */
    const handleRequirementAction = (action) => {
        if (action.kind === REQUIREMENT_ACTION_KINDS.ADMIN_SETTINGS) {
            parentWindowDataChannel.sendNavigateToAdmin(action.destination);
            return;
        }

        if (action.kind === REQUIREMENT_ACTION_KINDS.BILLING_ROUTE) {
            history.push(action.route);
        }
    };

    return (
        <>
            {isLastStepAndNotEntryProduct ? (
                <section className={`plan-card${requirementsViewModel ? ' plan-blocked' : ''}`}>
                    <header>
                        <h3>{productName}</h3>
                        <ContactTeam />
                        {requirementsViewModel && (
                            <PlanRequirementsModal
                                viewModel={requirementsViewModel}
                                productName={productName}
                                onRequirementAction={handleRequirementAction}
                            />
                        )}
                    </header>
                    <ul className="plan-features">
                        {productFeatures?.length > 0 && <Features features={productFeatures} />}
                    </ul>
                    {addonFeatures?.length > 0 && (
                        <ul className="plan-addons">
                            <Features features={addonFeatures} />
                        </ul>
                    )}
                </section>
            ) : (
                <section
                    className={`plan-card${cardStateClass}${isRecommended ? ' plan-recommended' : ''}`}
                >
                    {isRecommended && (
                        <span className="plan-recommended-badge" style={{ userSelect: 'none' }}>
                            {CARD_COPY.recommendedBadge}
                        </span>
                    )}
                    <header>
                        <h3>{productName}</h3>
                        <div className="price">
                            <div className="price-sym">$</div>
                            <div className="price-amount">{cost}</div>
                            <div className="price-term">/mo</div>
                        </div>
                        {/* The current plan always renders as the disabled button:
                            requirements to "switch to" the plan the user is on
                            make no sense, even when blockers technically fire */}
                        {isCurrentPlanCard && (
                            <button
                                className="gh-btn gh-btn-block plan-requirements-current-button"
                                data-test-btn={`current-${productName}`}
                                disabled
                            >
                                {CARD_COPY.currentPlan}
                            </button>
                        )}
                        {!isCurrentPlanCard && requirementsViewModel && (
                            <PlanRequirementsModal
                                viewModel={requirementsViewModel}
                                productName={productName}
                                onRequirementAction={handleRequirementAction}
                            />
                        )}
                        {!isCurrentPlanCard && !requirementsViewModel && validationMsg && (
                            <div className="price-unavailable">
                                <p>{validationMsg}</p>
                            </div>
                        )}
                        {!isLastIncrementStep && !validationMsg && (
                            <button
                                className="gh-btn gh-btn-block"
                                data-test-btn={productName}
                                disabled={btnDisabled}
                                onClick={handleSelect}
                            >
                                Choose this plan
                            </button>
                        )}
                    </header>
                    <ul className="plan-features">
                        {productFeatures?.length > 0 && <Features features={productFeatures} />}
                    </ul>
                    {addonFeatures?.length > 0 && (
                        <ul className="plan-addons">
                            <Features features={addonFeatures} />
                        </ul>
                    )}
                </section>
            )}
        </>
    );
}

Product.propTypes = {
    productName: PropTypes.string,
    stripeProductId: PropTypes.string,
    features: PropTypes.array,
    prices: PropTypes.array,
    showPeriod: PropTypes.string,
    handlePlanSelected: PropTypes.func,
    currentPrice: PropTypes.object,
    currentSiteLimits: PropTypes.object,
    isSubdirectory: PropTypes.bool,
    disabled: PropTypes.bool,
    currentGhostVersion: PropTypes.object,
    limits: PropTypes.array,
    addons: PropTypes.array,
    currentMembers: PropTypes.number,
    hasCustomSendingDomain: PropTypes.bool,
    availableProducts: PropTypes.array,
    currentPlanName: PropTypes.string,
    recommendedPlanName: PropTypes.string,
    recommendedIsCurrentPlan: PropTypes.bool,
    isRecommended: PropTypes.bool,
};
